<template>
    <b-modal v-model="isModalActive" :id="'sideBar' + $store.state.sidebar" :can-cancel="false" :width="1000">
        <div class="form-modal-widget">
            <div class="form-modal-header">
                <h1>MANUAL VOTES HISTORY</h1>
                <div class="header-info" v-if="session">
                    <span>{{ session.title }}</span>
                </div>
            </div>

            <div class="form-modal-body">
                <div class="loading-state" v-if="loading">
                    <b-loading :is-full-page="false" v-model="loading"></b-loading>
                </div>

                <div v-else-if="manualVotesHistory.length > 0" class="history-content">
                    <!-- Stats Summary -->
                    <div class="stats-summary">
                        <div class="stat-item">
                            <div class="stat-label">Total Manual Votes Added</div>
                            <div class="stat-value">{{ totalManualVotes }}</div>
                        </div>
                        <div class="stat-item">
                            <div class="stat-label">Total Entries</div>
                            <div class="stat-value">{{ manualVotesHistory.length }}</div>
                        </div>
                        <div class="stat-item">
                            <div class="stat-label">Participants Affected</div>
                            <div class="stat-value">{{ uniqueParticipants }}</div>
                        </div>
                    </div>

                    <!-- History Table -->
                    <div class="history-table">
                        <b-table :data="manualVotesHistory" :loading="loading">

                            <b-table-column field="participant" label="Participant" v-slot="props">
                                <div class="participant-cell">
                                    <div class="participant-image">
                                        <img v-if="props.row.participant_image"
                                            :src="$store.state.ASSETS_BASE_URL + '/assets/uploaded/' + props.row.participant_image" />
                                        <img v-else :src="$store.state.ASSETS_BASE_URL +
                                            '/assets/uploaded/' +
                                            session.logo
                                            " />
                                    </div>
                                    <div class="participant-info">
                                        <strong>{{ props.row.participant_name }}</strong>
                                    </div>
                                </div>
                            </b-table-column>

                            <b-table-column field="votes" label="Votes Added" v-slot="props" numeric>
                                <b-tag type="is-success" size="is-medium">+{{ props.row.votes }}</b-tag>
                            </b-table-column>

                            <b-table-column field="reason" label="Reason" v-slot="props">
                                <span v-if="props.row.reason">{{ props.row.reason }}</span>
                                <span v-else class="has-text-grey">No reason provided</span>
                            </b-table-column>

                            <b-table-column field="created_at" label="Date Added" v-slot="props">
                                <div class="date-cell">
                                    <div>{{ moment(props.row.created_at).format("MMM DD, YYYY") }}</div>
                                    <small>{{ moment(props.row.created_at).format("h:mm A") }}</small>
                                </div>
                            </b-table-column>

                            <b-table-column field="added_by" label="Added By" v-slot="props">
                                <span class="has-text-grey">Owner</span>
                            </b-table-column>

                            <template #empty>
                                <div class="empty-state">
                                    <b-icon icon="vote" size="is-large" type="is-grey-light"></b-icon>
                                    <p>No manual votes found</p>
                                </div>
                            </template>
                        </b-table>
                    </div>
                </div>

                <div v-else class="empty-state">
                    <b-icon icon="vote" size="is-large" type="is-grey-light"></b-icon>
                    <h3>No Manual Votes</h3>
                    <p>No manual votes have been added to this voting session yet.</p>
                </div>
            </div>

            <div class="form-modal-footer">
                <b-button @click="exportHistory" type="is-info" outlined :loading="exporting">
                    <b-icon icon="download"></b-icon>
                    <span>Export CSV</span>
                </b-button>
                <b-button @click="onClose" type="is-primary">CLOSE</b-button>
            </div>
        </div>
    </b-modal>
</template>

<script>
export default {
    components: {},
    props: {
        session: {
            type: Object,
            default: null,
        },
    },
    data: () => ({
        isModalActive: true,
        loading: false,
        exporting: false,
        votesHistory: [],
    }),
    computed: {
        manualVotesHistory() {
            return this.votesHistory
                .filter(item => item.type === 'manual')
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        },
        totalManualVotes() {
            return this.manualVotesHistory.reduce((sum, item) => sum + parseInt(item.votes), 0);
        },
        uniqueParticipants() {
            const participants = new Set(this.manualVotesHistory.map(item => item.participant_id));
            return participants.size;
        }
    },
    watch: {},
    mounted() {
        let vm = this;
        vm.loadVotesHistory();
    },
    methods: {
        loadVotesHistory() {
            let vm = this;
            if (!vm.session) return;

            vm.loading = true;
            vm.$store
                .dispatch("VOTING_GET_ADDED_VOTES_HISTORY", {
                    session_id: vm.session.id
                })
                .then((response) => {
                    vm.loading = false;
                    vm.votesHistory = response.data.return || [];
                })
                .catch(({ response }) => {
                    vm.loading = false;
                    vm.$notify({
                        group: "status",
                        type: "warn",
                        title: "Error",
                        text: response.data.message || "Failed to load votes history",
                    });
                });
        },
        exportHistory() {
            let vm = this;
            vm.exporting = true;

            // Create CSV content
            const headers = ['Participant', 'Votes Added', 'Reason', 'Date', 'Added By'];
            const csvContent = [
                headers.join(','),
                ...vm.manualVotesHistory.map(item => [
                    `"${item.participant_name}"`,
                    item.votes,
                    `"${item.reason || 'No reason provided'}"`,
                    vm.moment(item.created_at).format("YYYY-MM-DD HH:mm:ss"),
                    `"${item.added_by_name || 'System'}"`
                ].join(','))
            ].join('\n');

            // Download CSV
            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `manual-votes-history-${vm.session.title}-${vm.moment().format('YYYY-MM-DD')}.csv`;
            link.click();
            window.URL.revokeObjectURL(url);

            vm.exporting = false;
        },
        onClose() {
            let vm = this;
            vm.$emit("onClose");
        },
    },
};
</script>

<style lang="scss" scoped>
.form-modal-widget {
    width: 1000px;
    background: #fff;
    margin: 20px auto;
    border-radius: 5px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;

    @media (max-width: 1050px) {
        width: 95% !important;
    }

    @media (max-width: 500px) {
        width: 100% !important;
        margin: 0;
        max-height: 100vh;
    }

    .form-modal-header {
        height: 60px;
        display: flex;
        align-items: center;
        border-bottom: 1px solid #ddd;
        padding: 0px 20px;
        justify-content: space-between;
        flex-shrink: 0;

        @media (max-width: 500px) {
            position: sticky;
            top: 0;
            z-index: 10;
            background: white;
        }

        h1 {
            font-size: 16px;
            font-weight: 600;
            color: #333;
        }

        .header-info {
            font-size: 14px;
            color: #666;
        }
    }

    .form-modal-body {
        padding: 20px;
        overflow-y: auto;
        flex: 1;
        position: relative;

        .loading-state {
            height: 200px;
            position: relative;
        }

        .stats-summary {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 15px;
            margin-bottom: 25px;

            .stat-item {
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                color: white;
                padding: 20px;
                border-radius: 10px;
                text-align: center;

                .stat-label {
                    font-size: 14px;
                    opacity: 0.9;
                    margin-bottom: 8px;
                }

                .stat-value {
                    font-size: 24px;
                    font-weight: 700;
                }
            }
        }

        .history-table {
            background: white;
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

            .participant-cell {
                display: flex;
                align-items: center;

                .participant-image {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    overflow: hidden;
                    margin-right: 12px;
                    flex-shrink: 0;

                    img {
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                    }
                }

                .participant-info {
                    flex: 1;
                }
            }

            .date-cell {
                text-align: right;

                small {
                    color: #666;
                }
            }

            .detail-content {
                background: #f8f9fa;
                padding: 15px;
                border-radius: 6px;

                .detail-row {
                    margin-bottom: 8px;

                    &:last-child {
                        margin-bottom: 0;
                    }

                    strong {
                        color: #333;
                        margin-right: 8px;
                    }
                }
            }

            .empty-state {
                text-align: center;
                padding: 40px 20px;
                color: #666;

                p {
                    margin-top: 10px;
                    font-size: 14px;
                }
            }
        }


        .empty-state {
            text-align: center;
            padding: 60px 20px;
            color: #666;

            h3 {
                margin: 15px 0 10px 0;
                color: #333;
            }

            p {
                font-size: 14px;
            }
        }
    }

    .form-modal-footer {
        height: 60px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        border-top: 1px solid #ddd;
        padding: 0px 20px;
        flex-shrink: 0;
    }
}

#sideBartrue {
    left: 230px;

    @media (max-width: 768px) {
        left: 0;
    }

    .form-modal-widget {
        @media (max-width: 768px) {
            width: 95%;
        }
    }
}
</style>
